<script setup lang="ts">
/* The left column of the confirmed 1C-2 shell: the Studio mark, the workspace
 * switcher, and the publications of the workspace grouped by type, with the
 * stages of the open one under it.
 *
 * The measurements are the design's own, from
 * ~/Claude/plans/studio-substance-review-2026-11-03/page/index.html:
 * a 248px column beside the screen, the menu's own warm ground with a
 * hairline on its right, 18px of padding, 8px rows with an 8px radius,
 * 22x28 covers, the stages indented to 64px, a 26px icon square with a 16px
 * stroke at 1.9, and the folded rail at 60px with the current cover ringed
 * in the accent.
 *
 * Every colour here is a token (row R1): --menu-ground, --menu-label,
 * --menu-on-bg, --menu-on-ink and the four module colours. */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, type RouteLocationRaw } from "vue-router";
import { request } from "@/lib/api";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher.vue";
import { currentIssueVersion, type SummaryIssue } from "@/lib/issue-summary";
import { renderAssetUrl } from "@/lib/render-manifest";
import { isNarrowMenu, menuFoldShortcut, readMenuFold, writeMenuFold, type MenuFoldScope } from "@/lib/menu-fold";
import type { PublicationSummary } from "@/lib/publication-route";

const props = withDefaults(defineProps<{
  workspaceId: string;
  issues: PublicationSummary[];
  userId: string;
  scope: MenuFoldScope;
  maySeeAdmin: boolean;
  /* The phone tier: the menu has no column there and is a drawer over the
   * page instead. It is always the open menu in that tier, never the rail. */
  phone?: boolean;
  /* Whether that drawer is open. The shell owns it, because the button that
   * opens it is in the shell's top bar. */
  drawerOpen?: boolean;
}>(), { phone: false, drawerOpen: false });

const emit = defineEmits<{ role: [string]; close: [] }>();

const route = useRoute();

/* The icon paths are the design's own, lifted from its `finalize()` map. */
const ICONS: Record<string, string[]> = {
  magazines: ["M3 5c3-1 6-1 9 1v14c-3-2-6-2-9-1z", "M21 5c-3-1-6-1-9 1v14c3-2 6-2 9-1z"],
  handouts: ["M7 3h7l4 4v14H7z", "M14 3v4h4", "M10 12h5", "M10 16h5"],
  people: ["M9 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7", "M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6", "M16 4.6a3.5 3.5 0 0 1 0 6.8", "M21 20c0-2.6-1.6-4.8-4-5.6"],
  activity: ["M3 12h4l3-8 4 16 3-8h4"],
};

function kindOf(issue: PublicationSummary): "magazine" | "handout" {
  return issue.kind === "handout" ? "handout" : "magazine";
}

/* Magazines read newest first by number; handouts have no number, so they read
 * newest first by their press date. */
const magazines = computed(() => props.issues.filter((issue) => kindOf(issue) === "magazine").sort((a, b) => (b.number ?? 0) - (a.number ?? 0)));
const handouts = computed(() => props.issues.filter((issue) => kindOf(issue) === "handout").sort((a, b) => String(b.publishDate ?? "").localeCompare(String(a.publishDate ?? ""))));

const magazineInRoute = computed(() => {
  const number = typeof route.params.number === "string" ? route.params.number : null;
  if (number === null) return null;
  return magazines.value.find((issue) => issue.number !== null && String(issue.number) === number) ?? null;
});
const handoutInRoute = computed(() => {
  const id = typeof route.params.id === "string" ? route.params.id : null;
  if (id === null) return null;
  return handouts.value.find((issue) => issue.id === id) ?? null;
});

/* The open publication: the one you are in, or the newest magazine when you
 * are in no publication at all. Its stages are the only ones drawn. */
const openMagazine = computed(() => magazineInRoute.value ?? (handoutInRoute.value ? null : magazines.value[0] ?? null));
const openHandout = computed(() => handoutInRoute.value);

/* The open one and the one before it. */
function pair(list: PublicationSummary[], open: PublicationSummary | null): PublicationSummary[] {
  if (list.length === 0) return [];
  if (!open) return list.slice(0, 2);
  const index = list.findIndex((issue) => issue.id === open.id);
  return index === -1 ? list.slice(0, 2) : list.slice(index, index + 2);
}
const shownMagazines = computed(() => pair(magazines.value, openMagazine.value));
const shownHandouts = computed(() => pair(handouts.value, openHandout.value));

const landing = computed<RouteLocationRaw>(() => ({ name: "workspace", params: { ws: props.workspaceId } }));
const membersLink = computed<RouteLocationRaw>(() => ({ name: "members", params: { ws: props.workspaceId } }));
const historyLink = computed<RouteLocationRaw>(() => ({ name: "history", params: { ws: props.workspaceId } }));

function issueTarget(issue: PublicationSummary): RouteLocationRaw {
  return kindOf(issue) === "handout"
    ? { name: "handout-home", params: { ws: props.workspaceId, id: issue.id } }
    : { name: "issue-home", params: { ws: props.workspaceId, number: issue.number } };
}
function reviewTarget(issue: PublicationSummary): RouteLocationRaw {
  return kindOf(issue) === "handout"
    ? { name: "handout-review", params: { ws: props.workspaceId, id: issue.id } }
    : { name: "review", params: { ws: props.workspaceId, number: issue.number } };
}
/* The stage you are on, so it can carry the current fill and ink. */
const currentStage = computed<"overview" | "review" | null>(() => {
  const name = typeof route.name === "string" ? route.name : "";
  if (name === "issue-home" || name === "handout-home") return "overview";
  if (name === "review" || name === "review-version" || name === "handout-review" || name === "handout-review-version" || name === "upload-report" || name === "handout-upload-report") return "review";
  return null;
});

function issueLabel(issue: PublicationSummary): string {
  const title = issue.title ?? "";
  return kindOf(issue) === "handout" ? title : `Issue ${issue.number ?? ""}${title ? ` · ${title}` : ""}`;
}

/* The covers. Only the publications the menu actually shows are fetched, at
 * most two per type, and a cover that will not load falls back to a tile. */
const covers = ref<Record<string, string | null>>({});
let coverGeneration = 0;
const shownIssues = computed(() => [...shownMagazines.value, ...shownHandouts.value]);

watch(
  () => `${props.workspaceId}:${shownIssues.value.map((issue) => issue.id).join(",")}`,
  async () => {
    const generation = ++coverGeneration;
    covers.value = {};
    for (const issue of shownIssues.value) {
      try {
        const detail = await request<{ issue?: SummaryIssue }>(`/api/workspaces/${props.workspaceId}/issues/${issue.id}`);
        const version = currentIssueVersion(detail.issue);
        if (generation !== coverGeneration) return;
        covers.value = { ...covers.value, [issue.id]: version && typeof version.gen === "number" ? renderAssetUrl(version.id, version.gen, 1, "thumb") : null };
      } catch {
        if (generation !== coverGeneration) return;
        covers.value = { ...covers.value, [issue.id]: null };
      }
    }
  },
  { immediate: true },
);

function coverFor(issue: PublicationSummary): string | null {
  const cover = covers.value[issue.id];
  return cover === undefined ? null : cover;
}
function coverFailed(issue: PublicationSummary): void {
  covers.value = { ...covers.value, [issue.id]: null };
}
function tileLabel(issue: PublicationSummary): string {
  return kindOf(issue) === "handout" ? "H" : String(issue.number ?? "");
}

/* The fold. Remembered per person and per screen family, so the proof page
 * can be folded while every other page stays open. */
const folded = ref(false);
const narrow = ref(false);

/* The phone tier. The menu is a drawer there: it has no column, it is always
 * the open menu and never the rail, and nothing about it is remembered. */
const drawer = computed(() => props.phone);
const drawerOpen = computed(() => props.drawerOpen);
const rail = computed(() => folded.value && !drawer.value);
const nav = ref<HTMLElement | null>(null);

function loadFold(): void {
  narrow.value = isNarrowMenu();
  folded.value = narrow.value ? true : readMenuFold(props.userId, props.scope);
}
watch(() => [props.userId, props.scope], loadFold, { immediate: true });
watch(folded, (value) => {
  /* The fold is a setting and is remembered. The drawer is a state of the
   * page and is not: nothing about it is written down, so the phone tier
   * writes nothing at all. */
  if (narrow.value || drawer.value) return;
  writeMenuFold(props.userId, props.scope, value);
});

function toggleFold(): void {
  folded.value = !folded.value;
}

/* The one button at the top of the menu. At the phone width there is no fold
 * to do, so the same button closes the drawer. */
function onFoldClick(): void {
  if (drawer.value) {
    emit("close");
    return;
  }
  toggleFold();
}

function onResize(): void {
  const isNarrow = isNarrowMenu();
  if (isNarrow === narrow.value) return;
  narrow.value = isNarrow;
  folded.value = isNarrow ? true : readMenuFold(props.userId, props.scope);
}

/* The controls inside the menu, in the order the eye reads them. */
function focusableIn(root: HTMLElement): HTMLElement[] {
  const found = root.querySelectorAll<HTMLElement>("a[href], button, input, select, textarea, [tabindex]");
  return [...found].filter((element) => !element.hasAttribute("disabled") && element.getAttribute("tabindex") !== "-1");
}

/* Tab stays inside the drawer while it is open. It is a drawer over the
 * page, so the page behind it must not be reachable with the keyboard while
 * the drawer is up: the last control wraps to the first, and the first wraps
 * back to the last. */
function trapTab(event: KeyboardEvent): void {
  const root = nav.value;
  if (!root) return;
  const items = focusableIn(root);
  if (items.length === 0) return;
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement as HTMLElement | null;
  const inside = active !== null && root.contains(active);
  if (event.shiftKey) {
    if (!inside || active === first) {
      event.preventDefault();
      last.focus();
    }
    return;
  }
  if (!inside || active === last) {
    event.preventDefault();
    first.focus();
  }
}

/* The one key that folds the menu. The proof page's own keys are `[` and `]`,
 * which fold the tools and the comment rail, and they stay theirs. */
function onKeydown(event: KeyboardEvent): void {
  /* At the phone width there is no fold: the shell's own key opens and closes
   * the drawer, and Tab is kept inside it. */
  if (drawer.value) {
    if (drawerOpen.value && event.key === "Tab") trapTab(event);
    return;
  }
  if (!menuFoldShortcut(event)) return;
  event.preventDefault();
  toggleFold();
}

/* The focus goes into the drawer when it opens, onto its first control. */
watch(drawerOpen, async (open) => {
  if (!open) return;
  await nextTick();
  const root = nav.value;
  if (!root) return;
  focusableIn(root)[0]?.focus();
});

onMounted(() => {
  window.addEventListener("keydown", onKeydown);
  window.addEventListener("resize", onResize);
});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  window.removeEventListener("resize", onResize);
});
</script>

<template>
  <nav
    v-show="!drawer || drawerOpen"
    id="side-menu"
    ref="nav"
    class="side-menu"
    :class="{ 'side-menu--folded': rail, 'side-menu--drawer': drawer }"
    aria-label="Studio menu"
    :aria-hidden="drawer && !drawerOpen ? 'true' : undefined"
  >
    <div class="side-menu__top">
      <span class="side-menu__mark" aria-hidden="true">S</span>
      <span class="side-menu__word">Studio</span>
      <button
        class="side-menu__fold hit"
        type="button"
        :aria-expanded="drawer ? drawerOpen : !rail"
        aria-controls="side-menu-body"
        :title="drawer ? 'Close the menu' : (rail ? 'Open the menu' : 'Fold the menu')"
        :aria-label="drawer ? 'Close the menu' : undefined"
        @click="onFoldClick"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <path d="M9 4v16" />
          <path d="M15 10l-2 2 2 2" />
        </svg>
      </button>
    </div>

    <div class="side-menu__switcher">
      <WorkspaceSwitcher @role="emit('role', $event)" />
    </div>

    <div id="side-menu-body" class="side-menu__body">
      <section class="side-menu__group">
        <RouterLink class="side-menu__module" :to="landing" title="Magazines" aria-label="Magazines">
          <span class="side-menu__icon" style="--module: var(--mod-inprint)">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
              <path v-for="(d, index) in ICONS.magazines" :key="index" :d="d" />
            </svg>
          </span>
          <span class="side-menu__text">Magazines</span>
        </RouterLink>
        <template v-if="!rail">
          <template v-for="issue in shownMagazines" :key="issue.id">
            <RouterLink
              class="side-menu__issue"
              :class="{ 'side-menu__issue--current': issue.id === openMagazine?.id }"
              :to="issueTarget(issue)"
            >
              <span class="side-menu__cover">
                <img v-if="coverFor(issue)" :src="coverFor(issue) ?? ''" :alt="`Cover of ${issueLabel(issue)}`" @error="coverFailed(issue)" />
                <span v-else class="side-menu__tile" aria-hidden="true">{{ tileLabel(issue) }}</span>
              </span>
              <span class="side-menu__text">{{ issueLabel(issue) }}</span>
            </RouterLink>
            <div v-if="issue.id === openMagazine?.id" class="side-menu__stages">
              <RouterLink
                class="side-menu__stage"
                :class="{ 'side-menu__stage--current': currentStage === 'overview' }"
                :to="issueTarget(issue)"
              >
                Overview
              </RouterLink>
              <span class="side-menu__stage side-menu__stage--coming">Plan · coming</span>
              <span class="side-menu__stage side-menu__stage--coming">Content · coming</span>
              <RouterLink
                class="side-menu__stage"
                :class="{ 'side-menu__stage--current': currentStage === 'review' }"
                :to="reviewTarget(issue)"
              >
                Review
              </RouterLink>
            </div>
          </template>
          <RouterLink class="side-menu__all" :to="landing">All issues ({{ magazines.length }})</RouterLink>
        </template>
        <RouterLink
          v-else-if="openMagazine"
          class="side-menu__cover side-menu__cover--current"
          :to="issueTarget(openMagazine)"
          :title="issueLabel(openMagazine)"
          :aria-label="issueLabel(openMagazine)"
        >
          <img v-if="coverFor(openMagazine)" :src="coverFor(openMagazine) ?? ''" alt="" @error="coverFailed(openMagazine)" />
          <span v-else class="side-menu__tile" aria-hidden="true">{{ tileLabel(openMagazine) }}</span>
        </RouterLink>
      </section>

      <section class="side-menu__group">
        <RouterLink class="side-menu__module" :to="landing" title="Handouts" aria-label="Handouts">
          <span class="side-menu__icon" style="--module: var(--mod-handouts)">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
              <path v-for="(d, index) in ICONS.handouts" :key="index" :d="d" />
            </svg>
          </span>
          <span class="side-menu__text">Handouts</span>
        </RouterLink>
        <template v-if="!rail">
          <template v-for="issue in shownHandouts" :key="issue.id">
            <RouterLink
              class="side-menu__issue"
              :class="{ 'side-menu__issue--current': issue.id === openHandout?.id }"
              :to="issueTarget(issue)"
            >
              <span class="side-menu__cover">
                <img v-if="coverFor(issue)" :src="coverFor(issue) ?? ''" :alt="`Cover of ${issueLabel(issue)}`" @error="coverFailed(issue)" />
                <span v-else class="side-menu__tile" aria-hidden="true">{{ tileLabel(issue) }}</span>
              </span>
              <span class="side-menu__text">{{ issueLabel(issue) }}</span>
            </RouterLink>
            <div v-if="issue.id === openHandout?.id" class="side-menu__stages">
              <RouterLink
                class="side-menu__stage"
                :class="{ 'side-menu__stage--current': currentStage === 'overview' }"
                :to="issueTarget(issue)"
              >
                Overview
              </RouterLink>
              <span class="side-menu__stage side-menu__stage--coming">Plan · coming</span>
              <span class="side-menu__stage side-menu__stage--coming">Content · coming</span>
              <RouterLink
                class="side-menu__stage"
                :class="{ 'side-menu__stage--current': currentStage === 'review' }"
                :to="reviewTarget(issue)"
              >
                Review
              </RouterLink>
            </div>
          </template>
          <RouterLink class="side-menu__all" :to="landing">All handouts ({{ handouts.length }})</RouterLink>
        </template>
        <RouterLink
          v-else-if="openHandout"
          class="side-menu__cover side-menu__cover--current"
          :to="issueTarget(openHandout)"
          :title="issueLabel(openHandout)"
          :aria-label="issueLabel(openHandout)"
        >
          <img v-if="coverFor(openHandout)" :src="coverFor(openHandout) ?? ''" alt="" @error="coverFailed(openHandout)" />
          <span v-else class="side-menu__tile" aria-hidden="true">{{ tileLabel(openHandout) }}</span>
        </RouterLink>
      </section>

      <section v-if="maySeeAdmin" class="side-menu__group">
        <p class="side-menu__label">Workspace</p>
        <RouterLink class="side-menu__module" :to="membersLink" title="People" aria-label="People">
          <span class="side-menu__icon" style="--module: var(--mod-people)">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
              <path v-for="(d, index) in ICONS.people" :key="index" :d="d" />
            </svg>
          </span>
          <span class="side-menu__text">People</span>
        </RouterLink>
        <RouterLink class="side-menu__module" :to="historyLink" title="Activity" aria-label="Activity">
          <span class="side-menu__icon" style="--module: var(--mod-activity)">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
              <path v-for="(d, index) in ICONS.activity" :key="index" :d="d" />
            </svg>
          </span>
          <span class="side-menu__text">Activity</span>
        </RouterLink>
      </section>
    </div>
  </nav>
</template>

<style scoped>
.side-menu {
  position: sticky;
  top: 0;
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 248px;
  min-width: 0;
  /* The shell's own 3px rule across the top takes its 3px, so the menu is the
   * height of the screen without it and the two line up exactly. */
  height: calc(100vh - 3px);
  height: calc(100dvh - 3px);
  overflow-y: auto;
  padding: 18px 14px;
  background: var(--menu-ground);
  border-right: 1px solid var(--line);
}

.side-menu__top {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 8px 16px;
}

.side-menu__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: var(--primary-ink);
  color: var(--card);
  font-family: var(--font-head);
  font-size: 16px;
  font-style: italic;
  font-weight: 700;
  line-height: 1;
}

.side-menu__word {
  font-family: var(--font-head);
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.side-menu__fold {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-left: auto;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--card);
  color: var(--ink-2);
  cursor: pointer;
}
.side-menu__fold:hover { color: var(--ink); }
.side-menu__fold svg { fill: none; stroke: currentColor; stroke-width: 1.9; stroke-linecap: round; stroke-linejoin: round; }

.side-menu__switcher { margin-bottom: 12px; }
.side-menu__switcher :deep(.workspace-switcher__trigger) {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: none;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 9px;
  background: var(--card);
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 500;
}
.side-menu__switcher :deep(.workspace-switcher__menu) { left: 0; right: 0; width: auto; max-width: none; }

.side-menu__body { display: flex; flex-direction: column; gap: 3px; }
.side-menu__group { display: flex; flex-direction: column; gap: 3px; }

.side-menu__module {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 8px;
  border-radius: 8px;
  color: var(--ink);
  font-size: 14.5px;
  text-decoration: none;
}
.side-menu__module:hover { background: var(--card); }

.side-menu__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: color-mix(in srgb, var(--module) 16%, var(--card));
}
.side-menu__icon svg { fill: none; stroke: var(--module); stroke-width: 1.9; stroke-linecap: round; stroke-linejoin: round; }

.side-menu__issue {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px 6px 30px;
  border-radius: 8px;
  color: var(--ink);
  font-size: 13.5px;
  text-decoration: none;
}
.side-menu__issue:hover { background: var(--card); }
.side-menu__issue--current { background: var(--card); box-shadow: var(--shadow-card-soft); font-weight: 600; }

/* The name is ellipsised, so a long issue title does not push the row wider
 * than the column. */
.side-menu__text { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.side-menu__cover { display: inline-flex; flex: none; }
.side-menu__cover img { width: 22px; height: 28px; border-radius: 2px; object-fit: cover; box-shadow: var(--shadow-card-soft); }
.side-menu__tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 28px;
  border-radius: 2px;
  background: var(--line-2);
  color: var(--ink-2);
  font-size: 11px;
  font-weight: 600;
}

.side-menu__stages { display: flex; flex-direction: column; gap: 1px; }
.side-menu__stage {
  display: block;
  padding: 3px 10px 3px 64px;
  border-radius: 6px;
  color: var(--ink);
  font-size: 13px;
  text-decoration: none;
}
/* --menu-label, not --muted: --muted on --menu-ground is 4.27:1, under the 3 Nov 4.5:1 floor (R2 review). */
.side-menu__stage--coming { color: var(--menu-label); }
.side-menu__stage--current { background: var(--menu-on-bg); color: var(--menu-on-ink); font-weight: 600; }

.side-menu__all {
  padding: 4px 10px 6px 30px;
  color: var(--menu-on-ink);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
}

.side-menu__label {
  margin: 0;
  padding: 12px 10px 4px;
  color: var(--menu-label);
  font-size: 11.5px;
  font-weight: 600;
}

/* The folded rail: the mark, the module icons and the current cover, and
 * nothing else. The height is the same whatever the issue count. */
.side-menu--folded {
  align-items: center;
  width: 60px;
  padding: 16px 8px;
}
.side-menu--folded .side-menu__top { flex-direction: column; gap: 12px; padding: 0 0 14px; }
.side-menu--folded .side-menu__word,
.side-menu--folded .side-menu__switcher,
.side-menu--folded .side-menu__text,
.side-menu--folded .side-menu__label { display: none; }
.side-menu--folded .side-menu__fold { margin-left: 0; }
/* The panel icon turns round when the panel is folded. */
.side-menu--folded .side-menu__fold svg { transform: scaleX(-1); }
.side-menu--folded .side-menu__module { justify-content: center; padding: 5px; }
.side-menu--folded .side-menu__cover--current { padding: 3px; border-radius: 4px; outline: 2px solid var(--primary); outline-offset: 1px; }
.side-menu--folded .side-menu__cover--current img { width: 26px; height: 34px; }
.side-menu--folded .side-menu__cover--current .side-menu__tile { width: 26px; height: 34px; }

/* The phone tier's drawer (row R5).
 *
 * The menu has no column at this width: it leaves the flow entirely and comes
 * over the page from the left, full height, over the scrim the shell draws.
 * It is always the open menu (248px) and never the 60px rail, because a rail
 * in a drawer would be a drawer of nothing.
 *
 * The rule sits after .side-menu and .side-menu--folded because it has the same
 * weight as both and is the later truth at this width. */
.side-menu--drawer {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 248px;
  max-width: 100vw;
  height: 100vh;
  height: 100dvh;
  z-index: var(--z-overlay);
  box-shadow: var(--shadow-modal);
}
</style>
